import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Cpu, Brain, Lock, Zap, Database, X, Menu, ChevronRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ onDemoClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#overview", label: "Overview" },
    { href: "#capabilities", label: "Capabilities" },
    { href: "#solutions", label: "Solutions" },
    { href: "#pricing", label: "Pricing" },
  ];

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0E1A]/95 backdrop-blur-sm border-b border-[#2A3450]' : ''}`}
    >
      <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#" className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/images/63sats-logo.svg" alt="63SATS" className="h-10 w-auto" />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className="nav-link font-mono text-sm text-[#6B7A99] hover:text-[#00D4FF] transition-colors" data-testid={`nav-${link.label.toLowerCase()}`}>
              {link.label}
            </a>
          ))}
          <button onClick={onDemoClick} className="btn-primary text-sm py-3 px-6" data-testid="nav-demo-btn">
            Request Demo
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button className="md:hidden text-[#F5F3EE]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0A0E1A] border-b border-[#2A3450]"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="font-mono text-sm text-[#6B7A99] hover:text-[#00D4FF]" onClick={() => setMobileMenuOpen(false)}>
                  {link.label}
                </a>
              ))}
              <button onClick={() => { onDemoClick(); setMobileMenuOpen(false); }} className="btn-primary text-sm py-3 px-6 mt-2">
                Request Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Radar Component
const RadarAnimation = () => {
  const canvasRef = useRef(null);
  const [dataPoints, setDataPoints] = useState([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let angle = 0;
    const agentNodes = [0, 60, 120, 180, 240, 300].map(a => ({ angle: a, pulse: Math.random() }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const maxRadius = Math.min(centerX, centerY) - 20;

      // Draw hexagons
      [0.9, 0.6, 0.3].forEach((scale, i) => {
        ctx.beginPath();
        for (let j = 0; j < 6; j++) {
          const hexAngle = (j * 60 - 90 + (i === 1 ? angle * 0.5 : i === 2 ? -angle * 0.3 : angle * 0.2)) * Math.PI / 180;
          const x = centerX + Math.cos(hexAngle) * maxRadius * scale;
          const y = centerY + Math.sin(hexAngle) * maxRadius * scale;
          j === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(0, 212, 255, ${0.3 - i * 0.08})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Draw radial lines
      for (let i = 0; i < 8; i++) {
        const lineAngle = (i * 45) * Math.PI / 180;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(centerX + Math.cos(lineAngle) * maxRadius, centerY + Math.sin(lineAngle) * maxRadius);
        ctx.strokeStyle = 'rgba(0, 212, 255, 0.15)';
        ctx.stroke();
      }

      // Sweep line
      const sweepAngle = angle * Math.PI / 180;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX + Math.cos(sweepAngle) * maxRadius, centerY + Math.sin(sweepAngle) * maxRadius);
      ctx.strokeStyle = '#00D4FF';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Sweep glow trail
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, maxRadius, (angle - 30) * Math.PI / 180, angle * Math.PI / 180);
      ctx.lineTo(centerX, centerY);
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, maxRadius);
      gradient.addColorStop(0, 'rgba(0, 212, 255, 0.1)');
      gradient.addColorStop(1, 'rgba(0, 212, 255, 0.02)');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Agent nodes
      agentNodes.forEach((node, i) => {
        const nodeAngle = (node.angle + angle * 0.1) * Math.PI / 180;
        const x = centerX + Math.cos(nodeAngle) * maxRadius * 0.85;
        const y = centerY + Math.sin(nodeAngle) * maxRadius * 0.85;
        const pulseScale = 1 + Math.sin(Date.now() * 0.003 + i) * 0.3;
        
        ctx.beginPath();
        ctx.arc(x, y, 6 * pulseScale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${0.5 + Math.sin(Date.now() * 0.003 + i) * 0.3})`;
        ctx.fill();
        
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#00D4FF';
        ctx.fill();
      });

      // Center dot
      ctx.beginPath();
      ctx.arc(centerX, centerY, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#00D4FF';
      ctx.fill();

      angle = (angle + 1.5) % 360;
      animationId = requestAnimationFrame(draw);
    };

    draw();

    // Random data points
    const dataInterval = setInterval(() => {
      const chars = 'ABCDEF0123456789';
      const newPoint = {
        id: Date.now(),
        text: Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''),
        angle: Math.random() * 360,
        opacity: 1
      };
      setDataPoints(prev => [...prev.slice(-5), newPoint]);
    }, 1500);

    return () => {
      cancelAnimationFrame(animationId);
      clearInterval(dataInterval);
    };
  }, []);

  return (
    <div className="relative w-full h-full min-h-[400px]">
      <canvas ref={canvasRef} width={400} height={400} className="w-full h-full" />
      {dataPoints.map((point) => (
        <motion.div
          key={point.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{ duration: 3 }}
          className="absolute font-mono text-xs text-[#00D4FF]/60"
          style={{
            left: `${50 + Math.cos(point.angle * Math.PI / 180) * 45}%`,
            top: `${50 + Math.sin(point.angle * Math.PI / 180) * 45}%`,
          }}
        >
          {point.text}
        </motion.div>
      ))}
    </div>
  );
};

// Stats Counter Component
const StatsCounter = ({ value, label, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, value]);

  return (
    <div ref={ref} className="text-center px-4 py-6">
      <div className="font-display text-4xl md:text-5xl text-[#00D4FF] glow-cyan stat-number" data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
        {count}{suffix}
      </div>
      <div className="font-mono text-xs text-[#6B7A99] mt-2 tracking-widest uppercase">{label}</div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ onDemoClick }) => {
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const triggerGlitch = () => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 80);
    };
    triggerGlitch();
    const interval = setInterval(() => {
      if (Math.random() > 0.7) triggerGlitch();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="overview" className="min-h-screen flex flex-col pt-20 relative" data-testid="hero-section">
      <div className="flex-1 flex items-center">
        <div className="max-w-[1200px] mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center w-full">
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="section-tag mb-8"
            >
              // 63SATS CYBERTECH AI ORCHESTRATION DIVISION
            </motion.div>

            <div className={`glitch-text ${glitch ? 'active' : ''}`}>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.92] text-[#F5F3EE] mb-2"
                style={{ letterSpacing: '-1px' }}
                data-testid="hero-headline"
              >
                AUTONOMOUS
              </motion.h1>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.6 }}
                className="font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.92] text-[#F5F3EE] mb-2"
              >
                AI AGENT
              </motion.h1>
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.92] text-[#00D4FF] glow-cyan"
              >
                ORCHESTRATION
              </motion.h1>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="font-body text-[#6B7A99] text-base tracking-[3px] uppercase mt-8 mb-10"
            >
              Intelligence that works while you lead
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex flex-wrap gap-4"
            >
              <button onClick={onDemoClick} className="btn-primary" data-testid="hero-deploy-btn">
                Deploy CyberOps
              </button>
              <a href="#capabilities" className="btn-secondary" data-testid="hero-architecture-btn">
                View Architecture
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="lg:col-span-2 hidden lg:block"
          >
            <RadarAnimation />
          </motion.div>
        </div>
      </div>

      {/* Stats Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="border-t border-b border-[#2A3450] bg-[#0A0E1A]/80 mt-auto"
      >
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4">
          <StatsCounter value={259} suffix="+" label="MCP Tools" />
          <StatsCounter value={80} suffix="%" label="Cost Reduc." />
          <StatsCounter value={54} suffix="+" label="AI Agents" />
          <StatsCounter value={24} suffix="/7" label="Autonomous" />
        </div>
      </motion.div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const problems = [
    { num: "01", title: "Fragmented AI Usage", desc: "Scattered tools, inconsistent outputs, zero collaboration between AI systems." },
    { num: "02", title: "No Security-Grade Governance", desc: "AI decisions without audit trails. No compliance. No accountability." },
    { num: "03", title: "Analyst Bottlenecks", desc: "Your best people are stuck doing repetitive triage instead of strategic analysis." },
    { num: "04", title: "Token Burn Without ROI", desc: "Massive LLM costs with no visibility into value delivered per token." },
  ];

  return (
    <section className="py-24 relative" data-testid="problem-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-tag mb-4"
        >
          // THE CHALLENGE
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-12"
          data-testid="problem-title"
        >
          Your Enterprise is Drowning<br />in Manual AI Effort
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {problems.map((problem, i) => (
            <motion.div
              key={problem.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="problem-card"
              data-testid={`problem-card-${problem.num}`}
            >
              <span className="number">{problem.num}</span>
              <h3 className="font-heading font-semibold text-xl text-[#F5F3EE] mb-3">{problem.title}</h3>
              <p className="font-body text-[#6B7A99] leading-relaxed">{problem.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solution Section
const SolutionSection = () => {
  const features = [
    "Deploy swarms of specialized AI agents that collaborate",
    "Persistent memory for learning and improvement",
    "Hash-chained audit logs for traceability and compliance",
    "Intelligent model routing to cut token costs (up to 80%)",
    "Available as cloud SaaS, on-premise, or air-gapped",
  ];

  return (
    <section className="py-24 relative" data-testid="solution-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="section-tag mb-4"
            >
              // THE SOLUTION
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-6"
              data-testid="solution-title"
            >
              63SATS CyberOPs:<br />Your AI Workforce, Deployed
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="font-body text-[#6B7A99] text-lg leading-relaxed mb-8"
            >
              CyberOps is an enterprise-grade AI agent orchestration platform that transforms how organizations deploy, manage, and scale intelligent automation.
            </motion.p>

            <div className="space-y-4">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <ChevronRight className="text-[#00D4FF] mt-1 flex-shrink-0" size={18} />
                  <span className="font-body text-[#F5F3EE]">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Architecture Diagram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="bg-[#111827] border border-[#2A3450] p-8 rounded-lg">
              <div className="space-y-6">
                {/* Flow Boxes */}
                <div className="flex items-center justify-center gap-4">
                  <div className="border border-[#6B7A99] px-4 py-2 font-mono text-sm text-[#6B7A99]">CLIENT INPUT</div>
                  <div className="text-[#00D4FF]">→</div>
                  <div className="border border-[#00D4FF] px-4 py-2 font-mono text-sm text-[#00D4FF]">MCP GATEWAY</div>
                </div>
                
                <div className="flex justify-center">
                  <div className="text-[#00D4FF]">↓</div>
                </div>

                <div className="flex justify-center">
                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF] px-6 py-3 font-mono text-sm text-[#00D4FF]">CYBEROPS ROUTER</div>
                </div>

                <div className="flex justify-center gap-8">
                  <div className="text-[#00D4FF]">↙</div>
                  <div className="text-[#00D4FF]">↘</div>
                </div>

                <div className="flex items-center justify-center gap-8">
                  <div className="border border-[#F0A500] px-4 py-2 font-mono text-sm text-[#F0A500]">AGENT 1</div>
                  <div className="border border-[#F0A500] px-4 py-2 font-mono text-sm text-[#F0A500]">AGENT N</div>
                </div>

                <div className="flex justify-center">
                  <div className="text-[#00D4FF]">↓</div>
                </div>

                <div className="flex justify-center">
                  <div className="bg-[#1A2340] border border-[#2A3450] px-6 py-3 font-mono text-xs text-[#6B7A99] text-center">
                    SHARED MEMORY + PROOF CHAIN LEDGER
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="text-[#00D4FF]">↓</div>
                </div>

                <div className="flex justify-center">
                  <div className="bg-[#00D4FF] px-6 py-3 font-mono text-sm text-[#0A0E1A] font-semibold">VERIFIED OUTPUT</div>
                </div>
              </div>

              {/* Learning Loop Indicator */}
              <div className="absolute bottom-4 right-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#00D4FF] rounded-full animate-pulse"></span>
                <span className="font-mono text-xs text-[#00D4FF]">LEARNING LOOP ACTIVE</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Capabilities Section
const CapabilitiesSection = () => {
  const capabilities = [
    { id: "01", title: "Hive-Mind Swarm Intelligence", desc: "Multiple agents collaborate in real-time, sharing context and dividing complex tasks automatically.", tag: "MULTI-AGENT", icon: Brain },
    { id: "02", title: "Persistent Neural Memory", desc: "Agents remember past interactions, learn from outcomes, and improve over time.", tag: "MEMORY", icon: Database },
    { id: "03", title: "Enforcement Gate Layer", desc: "Policy enforcement before execution. No unauthorized actions. Ever.", tag: "GOVERNANCE", icon: Shield },
    { id: "04", title: "Proof-Chain Audit Ledger", desc: "Every decision hash-linked. Immutable audit trail for compliance and forensics.", tag: "COMPLIANCE", icon: Lock },
    { id: "05", title: "Intelligent Model Routing", desc: "Automatic selection of optimal LLM per task. Cut costs by up to 80%.", tag: "OPTIMIZATION", icon: Zap },
    { id: "06", title: "RAG + Domain Knowledge", desc: "Plug in your knowledge bases. Agents become domain experts instantly.", tag: "KNOWLEDGE", icon: Cpu },
  ];

  return (
    <section id="capabilities" className="py-24 relative" data-testid="capabilities-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-tag mb-4"
        >
          // CORE CAPABILITIES
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-12"
          data-testid="capabilities-title"
        >
          What CyberOps Delivers<br />Out of the Box
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <motion.div
                key={cap.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="capability-card"
                data-testid={`capability-card-${cap.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-sm text-[#6B7A99]">CAP {cap.id}</span>
                  <Icon className="text-[#00D4FF]" size={24} />
                </div>
                <div className="accent-bar mb-4"></div>
                <h3 className="font-heading font-semibold text-xl text-[#F5F3EE] mb-3">{cap.title}</h3>
                <p className="font-body text-[#6B7A99] leading-relaxed mb-4">{cap.desc}</p>
                <span className="font-mono text-xs text-[#00D4FF] border border-[#00D4FF] px-3 py-1">{cap.tag}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Terminal Demo Section
const TerminalDemo = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ agents: 0, vectors: 0, tasks: 0, cost: 0 });
  const [isRunning, setIsRunning] = useState(false);
  const logContainerRef = useRef(null);

  const logMessages = [
    { type: 'SPAWN', text: '[SPAWN] Agent-Alpha initialized → SOC Triage' },
    { type: 'MEMORY', text: '[MEMORY] Loading 2.4M vectors from knowledge base' },
    { type: 'TASK', text: '[TASK] Analyzing 847 security events...' },
    { type: 'COMM', text: '[COMM] Agent-Alpha → Agent-Beta: Sharing threat context' },
    { type: 'SPAWN', text: '[SPAWN] Agent-Beta initialized → Compliance Check' },
    { type: 'PROOF', text: '[PROOF] Hash: 0x7f3a...2d1b committed to ledger' },
    { type: 'TASK', text: '[TASK] Cross-referencing MITRE ATT&CK patterns' },
    { type: 'MEMORY', text: '[MEMORY] Storing analysis results → persistent memory' },
    { type: 'COMM', text: '[COMM] Agent-Beta → Router: Task complete, priority alerts: 3' },
    { type: 'COMPLETE', text: '[COMPLETE] Swarm task finished. 847 events → 3 critical alerts' },
  ];

  const runSimulation = () => {
    if (isRunning) return;
    setIsRunning(true);
    setLogs([]);
    setStats({ agents: 0, vectors: 0, tasks: 0, cost: 0 });

    let index = 0;
    const interval = setInterval(() => {
      if (index >= logMessages.length) {
        clearInterval(interval);
        setIsRunning(false);
        return;
      }
      const currentLog = logMessages[index];
      setLogs(prev => [...prev, currentLog]);
      setStats(prev => ({
        agents: Math.min(prev.agents + (Math.random() > 0.7 ? 1 : 0), 6),
        vectors: prev.vectors + Math.floor(Math.random() * 50000),
        tasks: prev.tasks + 1,
        cost: (parseFloat(prev.cost) + Math.random() * 0.02).toFixed(4)
      }));
      index++;
    }, 500);
  };

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const getLogColor = (type) => {
    switch (type) {
      case 'SPAWN': return 'text-[#00D4FF]';
      case 'MEMORY': return 'text-[#F0A500]';
      case 'PROOF': return 'text-green-400';
      case 'COMPLETE': return 'text-[#00D4FF] font-bold';
      case 'COMM': return 'text-[#6B7A99]';
      default: return 'text-[#F5F3EE]';
    }
  };

  return (
    <section className="py-24 relative" data-testid="demo-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-tag mb-4"
        >
          // LIVE SIMULATION
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-12"
        >
          See CyberOps in Action
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Terminal */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#111827] border border-[#2A3450] rounded-lg overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#2A3450]">
              <div className="w-3 h-3 rounded-full bg-[#FF3366]"></div>
              <div className="w-3 h-3 rounded-full bg-[#F0A500]"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="font-mono text-xs text-[#6B7A99] ml-2">agent_console.sh</span>
            </div>
            <div ref={logContainerRef} className="h-[300px] overflow-y-auto p-4 font-mono text-sm" data-testid="terminal-logs">
              {logs.length === 0 && (
                <div className="text-[#6B7A99]">
                  <span className="text-[#00D4FF]">$</span> Awaiting simulation start...
                  <span className="cursor-blink">_</span>
                </div>
              )}
              {logs.map((log, i) => (
                log && <div key={i} className={`${getLogColor(log.type)} mb-1`}>{log.text}</div>
              ))}
              {logs.length > 0 && !isRunning && (
                <div className="text-[#00D4FF] mt-2">
                  <span>$</span> <span className="cursor-blink">_</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Stats Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#111827] border border-[#2A3450] rounded-lg p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-sm text-[#6B7A99]">SWARM STATUS</span>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-[#6B7A99]'}`}></span>
                <span className="font-mono text-xs text-[#6B7A99]">{isRunning ? 'ACTIVE' : 'STANDBY'}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#0A0E1A] p-4 rounded">
                <div className="font-mono text-xs text-[#6B7A99] mb-1">ACTIVE AGENTS</div>
                <div className="font-display text-3xl text-[#00D4FF]" data-testid="stat-active-agents">{stats.agents}</div>
              </div>
              <div className="bg-[#0A0E1A] p-4 rounded">
                <div className="font-mono text-xs text-[#6B7A99] mb-1">MEMORY VECTORS</div>
                <div className="font-display text-3xl text-[#F0A500]">{stats.vectors.toLocaleString()}</div>
              </div>
              <div className="bg-[#0A0E1A] p-4 rounded">
                <div className="font-mono text-xs text-[#6B7A99] mb-1">TASKS COMPLETE</div>
                <div className="font-display text-3xl text-[#F5F3EE]">{stats.tasks}</div>
              </div>
              <div className="bg-[#0A0E1A] p-4 rounded">
                <div className="font-mono text-xs text-[#6B7A99] mb-1">TOKEN COST</div>
                <div className="font-display text-3xl text-green-400">${parseFloat(stats.cost).toFixed(4)}</div>
              </div>
            </div>

            {/* Mini Chart */}
            <div className="mb-6">
              <div className="font-mono text-xs text-[#6B7A99] mb-2">TASKS BY AGENT</div>
              <div className="flex items-end gap-2 h-16">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-[#00D4FF]/30 rounded-t transition-all duration-300"
                    style={{ height: `${Math.random() * 100}%`, opacity: i < stats.agents ? 1 : 0.2 }}
                  ></div>
                ))}
              </div>
            </div>

            <button
              onClick={runSimulation}
              disabled={isRunning}
              className={`w-full py-3 font-mono text-sm border ${isRunning ? 'border-[#2A3450] text-[#6B7A99]' : 'border-[#00D4FF] text-[#00D4FF] hover:bg-[#00D4FF] hover:text-[#0A0E1A]'} transition-all`}
              data-testid="run-simulation-btn"
            >
              [ {isRunning ? 'RUNNING...' : 'RUN SIMULATION'} ]
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Solutions Tabs Section
const SolutionsSection = () => {
  const [activeTab, setActiveTab] = useState('enterprise');

  const solutions = {
    enterprise: {
      label: 'ENTERPRISE · CSF',
      color: '#00D4FF',
      title: 'AI Agent Workforce for Enterprise Security',
      features: ['SOC triage and alert prioritization', 'Automated compliance reporting', 'Risk assessment and scoring', 'OT/ICS security monitoring', 'Threat intelligence correlation'],
      badge: null,
    },
    government: {
      label: 'GOVERNMENT · CYBERDOME',
      color: '#F0A500',
      title: 'Air-Gapped Sovereign AI for Defense',
      features: ['Threat intelligence synthesis', 'CERT-In compliant reporting', 'Anomaly detection across networks', 'Multi-agency coordination', 'Zero external dependency'],
      badge: 'AIR-GAPPED DEPLOYMENT',
    },
    digital: {
      label: 'DIGITAL ASSETS',
      color: '#F5F3EE',
      title: 'Intelligent Agents for Digital Asset Risk',
      features: ['On-chain address scoring', 'Portfolio risk rebalancing', 'Regulatory compliance automation', 'Market sentiment analysis', 'Smart contract auditing'],
      badge: null,
    },
  };

  return (
    <section id="solutions" className="py-24 relative" data-testid="solutions-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-tag mb-4"
        >
          // VERTICAL SOLUTIONS
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-12"
        >
          Tailored for Your Industry
        </motion.h2>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          {Object.entries(solutions).map(([key, sol]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-sm border transition-all ${activeTab === key ? 'border-[#00D4FF] bg-[#00D4FF]/10 text-[#00D4FF]' : 'border-[#2A3450] text-[#6B7A99] hover:border-[#6B7A99]'}`}
              data-testid={`tab-${key}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sol.color }}></span>
              {sol.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-[#111827] border border-[#2A3450] p-8 rounded-lg"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-display text-2xl text-[#F5F3EE] mb-4">{solutions[activeTab].title}</h3>
                {solutions[activeTab].badge && (
                  <span className="inline-block font-mono text-xs text-[#FF3366] border border-[#FF3366] px-3 py-1 mb-4">
                    {solutions[activeTab].badge}
                  </span>
                )}
                <div className="space-y-3">
                  {solutions[activeTab].features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Check className="text-[#00D4FF]" size={16} />
                      <span className="font-body text-[#F5F3EE]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-full h-48 bg-[#0A0E1A] border border-[#2A3450] rounded flex items-center justify-center">
                  <span className="font-mono text-xs text-[#6B7A99]">[ DASHBOARD PREVIEW ]</span>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onDemoClick }) => {
  const tiers = [
    {
      badge: 'POC',
      subtitle: 'One-Time Proof of Concept',
      features: ['1-week discovery workshop', '2-3 deployed agents', 'Cloud-hosted environment', 'Single use-case focus', '30-day hypercare support'],
      featured: false,
    },
    {
      badge: '★ RECOMMENDED',
      subtitle: 'Full Enterprise Deployment',
      features: ['4-6 week implementation', 'Up to 20 specialized agents', 'On-premise or private cloud', 'Full RAG integration', 'Custom governance policies', '12-month SLA coverage'],
      featured: true,
    },
    {
      badge: 'CLASSIFIED',
      subtitle: 'Air-Gapped Sovereign Deployment',
      features: ['Zero external dependency', 'Full data sovereignty', 'CERT-In/NIC compliance', 'Multi-agency coordination', 'Security clearance required', 'Annual retainer with SLA'],
      featured: false,
    },
  ];

  return (
    <section id="pricing" className="py-24 relative" data-testid="pricing-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="section-tag mb-4"
        >
          // DEPLOYMENT & PRICING
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-12"
        >
          Choose Your Deployment
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`bg-[#111827] p-8 rounded-lg ${tier.featured ? 'border-2 border-[#00D4FF] shadow-[0_0_30px_rgba(0,212,255,0.2)]' : 'border border-[#2A3450]'}`}
              data-testid={`pricing-tier-${i}`}
            >
              <span className={`inline-block font-mono text-xs px-3 py-1 mb-4 ${tier.featured ? 'bg-[#00D4FF] text-[#0A0E1A]' : 'border border-[#6B7A99] text-[#6B7A99]'}`}>
                {tier.badge}
              </span>
              <div className="font-mono text-sm text-[#6B7A99] mb-6">{tier.subtitle}</div>
              <div className="space-y-3 mb-8">
                {tier.features.map((feature, j) => (
                  <div key={j} className="flex items-center gap-3">
                    <Check className="text-[#00D4FF]" size={16} />
                    <span className="font-body text-sm text-[#F5F3EE]">{feature}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={onDemoClick}
                className={`w-full py-3 font-heading font-semibold uppercase tracking-wider ${tier.featured ? 'bg-[#00D4FF] text-[#0A0E1A]' : 'border border-[#00D4FF] text-[#00D4FF]'} hover:shadow-[0_0_20px_rgba(0,212,255,0.3)] transition-all`}
                data-testid={`pricing-cta-${i}`}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 text-center font-mono text-sm text-[#6B7A99]"
        >
          All deployments include: End-to-end encryption • Proof-chain audit logging • CERT-In alignment • NDA-covered engagement
        </motion.div>
      </div>
    </section>
  );
};

// Demo Request Modal
const DemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '', organization: '', designation: '', email: '', phone: '', deployment: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      await fetch(`${BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          organization: formData.organization,
          designation: formData.designation,
          email: formData.email,
          phone: formData.phone,
          deployment: formData.deployment,
          message: formData.message
        })
      });
    } catch (error) {
      console.error('Failed to submit demo request:', error);
    }
    
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  const handleClose = () => {
    setIsSuccess(false);
    setFormData({ fullName: '', organization: '', designation: '', email: '', phone: '', deployment: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#111827] border border-[#2A3450] rounded-lg p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
          data-testid="demo-modal"
        >
          {isSuccess ? (
            <div className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00D4FF]/20 flex items-center justify-center"
              >
                <Check className="text-[#00D4FF]" size={32} />
              </motion.div>
              <h3 className="font-display text-2xl text-[#F5F3EE] mb-4">Request Received</h3>
              <p className="font-body text-[#6B7A99] mb-6">Our team will contact you within 24 hours.</p>
              <button onClick={handleClose} className="btn-primary" data-testid="modal-close-btn">
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="section-tag">// SCHEDULE BRIEFING</div>
                <button onClick={handleClose} className="text-[#6B7A99] hover:text-[#F5F3EE]" data-testid="modal-x-btn">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Full Name *"
                  required
                  className="form-input w-full"
                  value={formData.fullName}
                  onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                  data-testid="input-fullname"
                />
                <input
                  type="text"
                  placeholder="Organization *"
                  required
                  className="form-input w-full"
                  value={formData.organization}
                  onChange={e => setFormData({ ...formData, organization: e.target.value })}
                  data-testid="input-organization"
                />
                <input
                  type="text"
                  placeholder="Designation *"
                  required
                  className="form-input w-full"
                  value={formData.designation}
                  onChange={e => setFormData({ ...formData, designation: e.target.value })}
                  data-testid="input-designation"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  className="form-input w-full"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  data-testid="input-email"
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  className="form-input w-full"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="input-phone"
                />
                <select
                  className="form-input w-full"
                  value={formData.deployment}
                  onChange={e => setFormData({ ...formData, deployment: e.target.value })}
                  required
                  data-testid="select-deployment"
                >
                  <option value="">Deployment Interest *</option>
                  <option value="saas">Enterprise SaaS</option>
                  <option value="onprem">On-Premise</option>
                  <option value="airgapped">Air-Gapped</option>
                  <option value="poc">PoC Only</option>
                </select>
                <textarea
                  placeholder="Message (optional)"
                  rows={3}
                  className="form-input w-full resize-none"
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  data-testid="input-message"
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full"
                  data-testid="submit-demo-btn"
                >
                  {isSubmitting ? 'Submitting...' : '[ SUBMIT REQUEST ]'}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Footer Section
const Footer = ({ onDemoClick }) => {
  return (
    <footer className="relative" data-testid="footer-section">
      {/* CTA Block */}
      <div className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-[#00D4FF]/10 via-transparent to-transparent opacity-50"></div>
        <div className="max-w-[1200px] mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-tag justify-center mb-4"
          >
            // READY TO DEPLOY
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl text-[#F5F3EE] mb-4"
          >
            Start Your AI Agent Workforce
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="font-body text-[#6B7A99] text-lg mb-8"
          >
            Schedule a confidential briefing with our AI orchestration team
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <button onClick={onDemoClick} className="btn-primary" data-testid="footer-demo-btn">
              Request Demo
            </button>
            <button className="btn-secondary" data-testid="footer-brief-btn">
              Download Tech Brief
            </button>
          </motion.div>
        </div>
      </div>

      {/* Contact Strip */}
      <div className="border-t border-b border-[#2A3450] py-8">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
          <div>
            <div className="font-mono text-xs text-[#6B7A99] mb-2">ENTERPRISE & GOVERNMENT</div>
            <a href="https://www.63sats.com" className="font-body text-[#00D4FF] hover:underline">www.63sats.com</a>
          </div>
          <div>
            <div className="font-mono text-xs text-[#6B7A99] mb-2">CONSUMER PLATFORM</div>
            <a href="https://www.cybx.in" className="font-body text-[#00D4FF] hover:underline">www.cybx.in</a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <img src="/images/63sats-logo.svg" alt="63SATS" className="h-14 w-auto" />
          </div>

          <div className="flex flex-wrap gap-6 justify-center">
            {['Overview', 'Capabilities', 'Solutions', 'Pricing', 'Contact'].map(link => (
              <a key={link} href={`#${link.toLowerCase()}`} className="font-body text-sm text-[#6B7A99] hover:text-[#00D4FF] transition-colors">
                {link}
              </a>
            ))}
          </div>

          <div className="text-right">
            <div className="font-body text-sm text-[#6B7A99]">63SATS Cybertech Pvt. Ltd.</div>
            <div className="font-mono text-xs text-[#6B7A99] mt-1">CSF · CyberDome · CYBX</div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#2A3450] py-6">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-wrap justify-between items-center gap-4">
          <div className="font-mono text-xs text-[#6B7A99]">
            © 2024 63SATS Cybertech Pvt. Ltd. All rights reserved.
          </div>
          <div className="flex gap-6">
            <a href="#" className="font-mono text-xs text-[#6B7A99] hover:text-[#00D4FF]">Privacy Policy</a>
            <a href="#" className="font-mono text-xs text-[#6B7A99] hover:text-[#00D4FF]">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="App grid-background relative">
      <div className="vignette"></div>
      <Navigation onDemoClick={() => setShowModal(true)} />
      <main className="relative z-10">
        <HeroSection onDemoClick={() => setShowModal(true)} />
        <ProblemSection />
        <SolutionSection />
        <CapabilitiesSection />
        <TerminalDemo />
        <SolutionsSection />
        <PricingSection onDemoClick={() => setShowModal(true)} />
        <Footer onDemoClick={() => setShowModal(true)} />
      </main>
      <DemoModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}

export default App;
